import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  Button,
  Badge,
  Text,
  InlineStack,
  BlockStack,
  TextField,
  Select
} from '@shopify/polaris';
import { openprojectService } from '../services/openprojectService';
import { parseDurationToMinutes, calculateTimeDifferenceMinutes, formatDateTime, getCommentText } from '../utils/timeFormat';
import { downloadCsv } from '../utils/csvExport';

const TimeEntries = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    user: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 50
  });

  useEffect(() => {
    loadUsers();
    loadTimeEntries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTimeEntries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  const loadUsers = async () => {
    try {
      const users = await openprojectService.getUsers();
      setUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadTimeEntries = async () => {
    setLoading(true);
    try {
      // Build proper OpenProject filters
      const openProjectFilters = [];
      
      // User filter
      if (filters.user) {
        openProjectFilters.push({
          user: {
            operator: '=',
            values: [filters.user]
          }
        });
      }
      
      // Date filters - convert to YYYY-MM-DD format
      if (filters.startDate || filters.endDate) {
        if (filters.startDate && filters.endDate) {
          // Range filter: between two dates
          const startDate = new Date(filters.startDate).toISOString().split('T')[0];
          const endDate = new Date(filters.endDate).toISOString().split('T')[0];
          
          openProjectFilters.push({
            spentOn: {
              operator: '<>d',
              values: [startDate, endDate]
            }
          });
        } else if (filters.startDate) {
          // Single date filter: from start date
          const startDate = new Date(filters.startDate).toISOString().split('T')[0];
          
          openProjectFilters.push({
            spentOn: {
              operator: '>=d',
              values: [startDate]
            }
          });
        } else if (filters.endDate) {
          // Single date filter: until end date
          const endDate = new Date(filters.endDate).toISOString().split('T')[0];
          
          openProjectFilters.push({
            spentOn: {
              operator: '<=d',
              values: [endDate]
            }
          });
        }
      }
      
      // Add pagination parameters
      const pageSize = pagination.pageSize;
      const offset = pagination.currentPage; // Page number = offset
      
      // Convert filters to OpenProject format
      const filtersString = JSON.stringify(openProjectFilters);

      const response = await openprojectService.getTimeEntries(filtersString, pageSize, offset);

      const entries = response._embedded?.elements || [];
      setTimeEntries(entries);
      
      // Update pagination info
      setPagination(prev => ({
        ...prev,
        totalCount: response.total || entries.length,
        totalPages: Math.ceil((response.total || entries.length) / pageSize)
      }));
    } catch (error) {
      console.error('Error loading time entries:', error);
      // Fallback: try to load without filters
      try {
        const response = await openprojectService.getTimeEntries('[]');
        setTimeEntries(response._embedded?.elements || []);
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
        setTimeEntries([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    loadTimeEntries();
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      user: '',
      startDate: today,
      endDate: today
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadTimeEntries();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const exportToCSV = () => {
    if (timeEntries.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = [
      'ID',
      'Start Time',
      'Stop Time',
      'User',
      'Task #',
      'Task Title',
      'Time Difference (minutes)',
      'Duration (minutes)',
      'Comment',
      'Status'
    ];

    const rows = timeEntries.map((entry) => {
      const timeDifference = calculateTimeDifferenceMinutes(entry.createdAt, entry.updatedAt);
      const isValid = isTimeValid(entry);
      const workPackageTitle = typeof entry._links?.workPackage?.title === 'string' ? entry._links.workPackage.title : 'N/A';
      const taskNumber = entry._links?.workPackage?.href ? entry._links.workPackage.href.split('/').pop() : 'N/A';
      const userTitle = typeof entry._links?.user?.title === 'string' ? entry._links.user.title : 'N/A';

      return [
        entry.id || 'N/A',
        formatDateTime(entry.createdAt),
        formatDateTime(entry.updatedAt),
        userTitle,
        taskNumber,
        workPackageTitle,
        timeDifference ? `${Math.round(timeDifference)} minutes` : 'N/A',
        `${parseDurationToMinutes(entry.hours)} minutes`,
        getCommentText(entry.comment),
        isValid ? 'Valid' : 'Invalid'
      ];
    });

    const dateStr = new Date().toISOString().split('T')[0];
    const userFilter = filters.user ? `_user_${filters.user}` : '';
    const dateFilter = filters.startDate ? `_from_${filters.startDate}` : '';
    const endDateFilter = filters.endDate ? `_to_${filters.endDate}` : '';

    downloadCsv(`time_entries${userFilter}${dateFilter}${endDateFilter}_${dateStr}.csv`, headers, rows);
  };

  const isTimeValid = (entry) => {
    const timeDifference = calculateTimeDifferenceMinutes(entry.createdAt, entry.updatedAt);
    const loggedMinutes = parseDurationToMinutes(entry.hours);

    if (!entry.createdAt || !entry.updatedAt) return false;

    // Allow small tolerance (1 minute)
    return Math.abs(timeDifference - loggedMinutes) <= 1;
  };

  const StatusCell = ({ isValid }) => (
    <Badge tone={isValid ? 'success' : 'critical'}>{isValid ? 'Valid' : 'Invalid'}</Badge>
  );

  const getTimeEntryRows = () => {
    return timeEntries.map((entry) => {
      const timeDifference = calculateTimeDifferenceMinutes(entry.createdAt, entry.updatedAt);
      const isValid = isTimeValid(entry);
      const workPackageTitle = typeof entry._links?.workPackage?.title === 'string' ? entry._links.workPackage.title : 'N/A';
      const taskNumber = entry._links?.workPackage?.href ? entry._links.workPackage.href.split('/').pop() : 'N/A';
      const userTitle = typeof entry._links?.user?.title === 'string' ? entry._links.user.title : 'N/A';

      return [
        entry.id || 'N/A',
        formatDateTime(entry.createdAt),
        formatDateTime(entry.updatedAt),
        userTitle,
        taskNumber,
        workPackageTitle,
        timeDifference ? `${Math.round(timeDifference)} minutes` : 'N/A',
        `${parseDurationToMinutes(entry.hours)} minutes`,
        getCommentText(entry.comment),
        <StatusCell isValid={isValid} />
      ];
    });
  };

  const userOptions = users.map(user => ({
    label: user.name || user.login,
    value: user.id.toString()
  }));

  return (
    <Page title="Time Entries">
      <Card sectioned>
        <BlockStack gap="400">
          <Text variant="headingMd">Filters</Text>
          
          <InlineStack gap="400" wrap={false}>
            <div style={{ minWidth: '200px' }}>
              <Select
                label="User"
                options={[
                  { label: 'All Users', value: '' },
                  ...userOptions
                ]}
                value={filters.user}
                onChange={(value) => handleFilterChange('user', value)}
              />
            </div>
            
            <div style={{ minWidth: '200px' }}>
              <TextField
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(value) => handleFilterChange('startDate', value)}
              />
            </div>
            
            <div style={{ minWidth: '200px' }}>
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(value) => handleFilterChange('endDate', value)}
              />
            </div>
          </InlineStack>
          
          <InlineStack gap="200">
            <Button variant="primary" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
            <Button onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Button onClick={exportToCSV} variant="primary">
              Export to CSV
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>

      <Card>
        <DataTable
          columnContentTypes={[
            'text',
            'text', 
            'text',
            'text',
            'text',
            'text',
            'text',
            'text',
            'text',
            'text'
          ]}
          headings={[
            'ID',
            'Start Time',
            'Stop Time',
            'User',
            'Task #',
            'Task Title',
            'Time Difference (minutes)',
            'Duration (minutes)',
            'Comment',
            'Status'
          ]}
          rows={getTimeEntryRows()}
          loading={loading}
        />
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card sectioned>
          <InlineStack align="space-between">
            <Text variant="bodyMd">
              Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} entries
            </Text>
            <InlineStack gap="200">
              <Button 
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                Previous
              </Button>
              <Text variant="bodyMd">
                Page {pagination.currentPage} of {pagination.totalPages}
              </Text>
              <Button 
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                Next
              </Button>
            </InlineStack>
          </InlineStack>
        </Card>
      )}
    </Page>
  );
};

export default TimeEntries;
